import { t, useLanguage } from '../i18n';
import React from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from 'react-native-paper';

import ScheduleScreen from '../screens/main/ScheduleScreen';
import CoursesScreen from '../screens/main/CoursesScreen';
import SettingsScreen from '../screens/main/SettingsScreen';
import ProfileScreen from '../screens/main/ProfileScreen';
import CourseDetailScreen from '../screens/main/CourseDetailScreen';
import AddCourseScreen from '../screens/main/AddCourseScreen';
import EditCourseScreen from '../screens/main/EditCourseScreen';
import AttendanceScreen from '../screens/main/AttendanceScreen';
import AssignmentsScreen from '../screens/main/AssignmentsScreen';
import MyCoursesScreen from '../screens/main/MyCoursesScreen';
import SchedulePreviewScreen from '../screens/main/SchedulePreviewScreen';
import SavedSchedulesScreen from '../screens/main/SavedSchedulesScreen';
import CourseEvaluationScreen from '../screens/main/CourseEvaluationScreen';
import CourseReviewFormScreen from '../screens/main/CourseReviewFormScreen';
import GradeSubmitScreen from '../screens/main/GradeSubmitScreen';
import ReviewManagementScreen from '../screens/main/ReviewManagementScreen';
import CalendarExportScreen from '../screens/main/CalendarExportScreen';
// import ImportLocalScreen from '../screens/main/ImportLocalScreen';

// Import providers
import { SyllabusProvider } from '../contexts/SyllabusContext';

const Tab = createBottomTabNavigator();
const Stack = createNativeStackNavigator();

const TabNavigator = () => {
  useLanguage();
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  return (
    <Tab.Navigator
      initialRouteName="Schedule"
      screenOptions={({ route }) => ({
        tabBarLabelPosition: isMobile ? 'below-icon' : 'beside-icon',
        tabBarIcon: ({ focused, color, size }) => {
          let iconName: any;

          if (route.name === 'Schedule') {
            iconName = focused ? 'calendar' : 'calendar-outline';
          } else if (route.name === 'Courses') {
            iconName = focused ? 'search' : 'search-outline';
          } else if (route.name === 'SavedSchedules') {
            iconName = focused ? 'folder' : 'folder-outline';
          } else if (route.name === 'Settings') {
            iconName = focused ? 'settings' : 'settings-outline';
          }

          return <Ionicons name={iconName} size={size} color={color} />;
        },
        tabBarActiveTintColor: theme.colors.primary,
        tabBarInactiveTintColor: 'gray',
        headerShown: false,
        tabBarStyle: {
          backgroundColor: theme.colors.surface,
          borderTopColor: theme.colors.outlineVariant,
          height: Platform.OS === 'ios' ? 80 : 60,
          paddingBottom: Platform.OS === 'ios' ? 25 : 8,
          paddingTop: 8,
          ...(Platform.OS === 'ios' && {
            marginBottom: 20,
            marginHorizontal: 16,
            borderRadius: 24,
            position: 'absolute',
            bottom: 25, // moved higher from 10
            left: 0,
            right: 0,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.1,
            shadowRadius: 12,
            elevation: 8,
          })
        }
      })}
    >
      <Tab.Screen name="Schedule" component={ScheduleScreen} options={{ title: t("時間割") }} />
      <Tab.Screen name="Courses" component={CoursesScreen} options={{ title: t("履修検索") }} />
      <Tab.Screen name="SavedSchedules" component={SavedSchedulesScreen} options={{ title: t("保存済み") }} />
      <Tab.Screen name="Settings" component={SettingsScreen} options={{ title: t("設定") }} />
    </Tab.Navigator>
  );
};

const MainNavigator = () => {
  useLanguage();
  const theme = useTheme();

  return (
    <SyllabusProvider>
      <Stack.Navigator
        screenOptions={{
          headerStyle: {
            backgroundColor: theme.colors.surface,
          },
          headerTintColor: theme.colors.onSurface,
          headerTitleStyle: {
            fontWeight: 'bold',
          },
        }}
      >
        <Stack.Screen
          name="TabRoot"
          component={TabNavigator}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="CourseDetail"
          component={CourseDetailScreen}
          options={{ title: t("授業詳細") }}
        />
        <Stack.Screen
          name="AddCourse"
          component={AddCourseScreen}
          options={{ title: t("授業追加") }}
        />
        <Stack.Screen
          name="EditCourse"
          component={EditCourseScreen}
          options={{ title: t("授業編集") }}
        />
        <Stack.Screen
          name="Attendance"
          component={AttendanceScreen}
          options={{ title: t("出席管理") }}
        />
        <Stack.Screen
          name="CalendarExport"
          component={CalendarExportScreen}
          options={{ title: t('Googleカレンダー') }}
        />
        <Stack.Screen
          name="Assignments"
          component={AssignmentsScreen}
          options={{ title: t("課題一覧") }}
        />
        <Stack.Screen
          name="Profile"
          component={ProfileScreen}
          options={{ title: t("プロフィール") }}
        />
        <Stack.Screen
          name="MyCourses"
          component={MyCoursesScreen}
          options={{ title: t("登録済みの授業") }}
        />
        <Stack.Screen
          name="SchedulePreview"
          component={SchedulePreviewScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="SavedSchedules"
          component={SavedSchedulesScreen}
          options={{ title: t("保存済み時間割") }}
        />
        <Stack.Screen
          name="CourseEvaluation"
          component={CourseEvaluationScreen}
          options={{ title: t("授業評価") }}
        />
        <Stack.Screen
          name="CourseReviewForm"
          component={CourseReviewFormScreen}
          options={{ title: t("レビュー投稿") }}
        />
        <Stack.Screen
          name="GradeSubmit"
          component={GradeSubmitScreen}
          options={{ title: t("成績報告") }}
        />
        <Stack.Screen
          name="ReviewManagement"
          component={ReviewManagementScreen}
          options={{ title: t("講義レビュー") }}
        />
        {/*
        <Stack.Screen
          name="ImportLocal"
          component={ImportLocalScreen}
          options={{ title: 'ローカルデータのインポート' }}
        />
        */}
      </Stack.Navigator>
    </SyllabusProvider>
  );
};

export default MainNavigator;
